import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Brain, Plus, Edit2, Trash2, Save, X, Search, 
  BookOpen, Shield, Star, AlertTriangle, Tag, Sparkles, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { COMPANY } from '../config/company';
import CompanyLogo from '../components/CompanyLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  compliance: { icon: Shield, color: 'bg-blue-100 text-blue-700', name: 'Florida Code Compliance' },
  satisfaction: { icon: Star, color: 'bg-green-100 text-green-700', name: 'Customer Satisfaction' },
  excellence: { icon: BookOpen, color: 'bg-purple-100 text-purple-700', name: 'Business Excellence' },
  prevention: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-700', name: 'Loss Prevention' },
  custom: { icon: Tag, color: 'bg-gray-100 text-gray-700', name: 'Custom' }
};

export default function AITraining() {
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [seeding, setSeeding] = useState(false);
  
  const [form, setForm] = useState({
    topic: '',
    keywords: [],
    question_pattern: '',
    response: '',
    category: 'custom',
    is_active: true,
    priority: 50
  });
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    fetchPatterns();
    fetchCategories();
  }, []);

  const fetchPatterns = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-training/patterns`);
      if (res.ok) {
        const data = await res.json();
        setPatterns(data);
      }
    } catch (error) {
      console.error('Error fetching patterns:', error);
      toast.error('Failed to load training patterns');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-training/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-training/seed-default-patterns`, {
        method: 'POST'
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchPatterns();
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error('Failed to seed default patterns');
    } finally {
      setSeeding(false);
    }
  };

  const openNewModal = () => {
    setEditingPattern(null);
    setForm({
      topic: '',
      keywords: [],
      question_pattern: '',
      response: '',
      category: 'custom',
      is_active: true,
      priority: 50
    });
    setKeywordInput('');
    setShowModal(true);
  };

  const openEditModal = (pattern) => {
    setEditingPattern(pattern);
    setForm({
      topic: pattern.topic || '',
      keywords: pattern.keywords || [],
      question_pattern: pattern.question_pattern || '',
      response: pattern.response || '',
      category: pattern.category || 'custom',
      is_active: pattern.is_active !== false,
      priority: pattern.priority || 50
    });
    setKeywordInput('');
    setShowModal(true);
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !form.keywords.includes(keywordInput.trim().toLowerCase())) {
      setForm({ ...form, keywords: [...form.keywords, keywordInput.trim().toLowerCase()] });
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setForm({ ...form, keywords: form.keywords.filter(k => k !== keyword) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.topic.trim()) {
      toast.error('Topic is required');
      return;
    }
    if (form.keywords.length === 0) {
      toast.error('At least one keyword is required');
      return;
    }
    if (!form.response.trim()) {
      toast.error('Response is required');
      return;
    }

    try {
      const url = editingPattern
        ? `${API_URL}/api/ai-training/patterns/${editingPattern.id}`
        : `${API_URL}/api/ai-training/patterns`;
      
      const res = await fetch(url, {
        method: editingPattern ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(editingPattern ? 'Pattern updated!' : 'Pattern created!');
        setShowModal(false);
        fetchPatterns();
      } else {
        toast.error('Failed to save pattern');
      }
    } catch (error) {
      toast.error('Error saving pattern');
    }
  };

  const handleDelete = async (pattern) => {
    if (!window.confirm(`Delete pattern "${pattern.topic}"?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ai-training/patterns/${pattern.id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Pattern deleted');
        fetchPatterns();
      } else {
        toast.error('Failed to delete pattern');
      }
    } catch (error) {
      toast.error('Error deleting pattern');
    }
  };

  const handleToggleActive = async (pattern) => {
    try {
      const res = await fetch(`${API_URL}/api/ai-training/patterns/${pattern.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !pattern.is_active })
      });
      if (res.ok) {
        toast.success(pattern.is_active ? 'Pattern disabled' : 'Pattern enabled');
        fetchPatterns();
      }
    } catch (error) {
      toast.error('Error updating pattern');
    }
  };

  // Filter patterns
  const filteredPatterns = patterns.filter(p => {
    const matchesSearch = !searchQuery || 
      p.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.keywords?.some(k => k.toLowerCase().includes(searchQuery.toLowerCase())) ||
      p.response?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const patternsByCategory = filteredPatterns.reduce((acc, p) => {
    const cat = p.category || 'custom';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <nav className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin" className="flex items-center text-gray-600 hover:text-gray-900">
                <ArrowLeft className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </Link>
              <div className="flex items-center space-x-2">
                <Brain className="h-6 w-6" style={{ color: COMPANY.primaryColor }} />
                <h1 className="text-xl font-bold text-gray-900">AI Training Module</h1>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {patterns.length === 0 && (
                <Button
                  onClick={handleSeedDefaults}
                  disabled={seeding}
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white"
                  data-testid="seed-defaults-btn"
                >
                  {seeding ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Load Default Patterns
                </Button>
              )}
              <Button
                onClick={openNewModal}
                style={{ backgroundColor: COMPANY.primaryColor }}
                className="text-white"
                data-testid="add-pattern-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Pattern
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6">
        {/* Description Card */}
        <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Brain className="h-8 w-8 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Train Your AI Assistant</h2>
                <p className="text-gray-600 mt-1">
                  Create custom Q&A patterns to teach the AI how to respond to specific questions. 
                  These patterns are checked first before the AI generates responses, ensuring accurate 
                  and consistent answers for your team.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                    <Badge key={key} className={info.color}>
                      <info.icon className="h-3 w-3 mr-1" />
                      {info.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search patterns by topic, keywords, or response..."
              className="pl-10"
              data-testid="search-patterns"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white text-gray-700"
            data-testid="filter-category"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          <Card className="text-center">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold" style={{ color: COMPANY.primaryColor }}>{patterns.length}</div>
              <div className="text-sm text-gray-500">Total Patterns</div>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-green-600">{patterns.filter(p => p.is_active).length}</div>
              <div className="text-sm text-gray-500">Active</div>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-blue-600">{patterns.filter(p => p.category === 'compliance').length}</div>
              <div className="text-sm text-gray-500">Compliance</div>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-purple-600">{patterns.filter(p => p.category === 'excellence').length}</div>
              <div className="text-sm text-gray-500">Excellence</div>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-orange-600">{patterns.filter(p => p.category === 'prevention').length}</div>
              <div className="text-sm text-gray-500">Prevention</div>
            </CardContent>
          </Card>
        </div>

        {/* Patterns List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-2 text-gray-500">Loading patterns...</p>
          </div>
        ) : filteredPatterns.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Brain className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No Training Patterns</h3>
              <p className="text-gray-500 mt-1">
                {patterns.length === 0 
                  ? "Click 'Load Default Patterns' to get started with pre-built patterns, or create your own."
                  : "No patterns match your search criteria."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(patternsByCategory).map(([category, categoryPatterns]) => {
              const catInfo = CATEGORY_INFO[category] || CATEGORY_INFO.custom;
              return (
                <div key={category}>
                  <div className="flex items-center space-x-2 mb-3">
                    <catInfo.icon className="h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                    <h3 className="text-lg font-semibold text-gray-900">{catInfo.name}</h3>
                    <Badge variant="secondary">{categoryPatterns.length}</Badge>
                  </div>
                  <div className="grid gap-4">
                    {categoryPatterns.map(pattern => (
                      <Card key={pattern.id} className={`transition-all ${!pattern.is_active ? 'opacity-60' : ''}`} data-testid={`pattern-card-${pattern.id}`}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="font-semibold text-gray-900">{pattern.topic?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h4>
                                {!pattern.is_active && <Badge variant="secondary">Disabled</Badge>}
                                <Badge className={catInfo.color}>{catInfo.name}</Badge>
                                <Badge variant="outline">Priority: {pattern.priority || 0}</Badge>
                              </div>
                              <div className="flex flex-wrap gap-1 mb-3">
                                {pattern.keywords?.map((keyword, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-gray-50">
                                    {keyword}
                                  </Badge>
                                ))}
                              </div>
                              {pattern.question_pattern && (
                                <p className="text-sm text-gray-500 italic mb-2">
                                  Example: "{pattern.question_pattern}"
                                </p>
                              )}
                              <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg max-h-32 overflow-y-auto whitespace-pre-wrap">
                                {pattern.response?.substring(0, 300)}{pattern.response?.length > 300 ? '...' : ''}
                              </div>
                            </div>
                            <div className="flex flex-col space-y-2 ml-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(pattern)}
                                data-testid={`edit-pattern-${pattern.id}`}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(pattern)}
                                className={pattern.is_active ? 'text-green-600' : 'text-gray-400'}
                              >
                                {pattern.is_active ? '✓' : '○'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(pattern)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-pattern-${pattern.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Brain className="h-5 w-5 mr-2" style={{ color: COMPANY.primaryColor }} />
                {editingPattern ? 'Edit Training Pattern' : 'New Training Pattern'}
              </CardTitle>
              <CardDescription>
                Define keywords that will trigger this response when users ask questions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Topic */}
                <div>
                  <Label>Topic / Title *</Label>
                  <Input
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    placeholder="e.g., florida_permit_requirements"
                    className="mt-1"
                    data-testid="pattern-topic"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use underscores for multi-word topics</p>
                </div>

                {/* Category */}
                <div>
                  <Label>Category</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    data-testid="pattern-category"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Keywords */}
                <div>
                  <Label>Keywords * (triggers this response)</Label>
                  <div className="flex space-x-2 mt-1">
                    <Input
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                      placeholder="Type keyword and press Enter"
                      data-testid="pattern-keyword-input"
                    />
                    <Button type="button" onClick={handleAddKeyword} variant="outline">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.keywords.map((keyword, i) => (
                      <Badge key={i} variant="secondary" className="cursor-pointer hover:bg-red-100" onClick={() => handleRemoveKeyword(keyword)}>
                        {keyword} <X className="h-3 w-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                  {form.keywords.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">Add at least one keyword</p>
                  )}
                </div>

                {/* Example Question */}
                <div>
                  <Label>Example Question (optional)</Label>
                  <Input
                    value={form.question_pattern}
                    onChange={(e) => setForm({ ...form, question_pattern: e.target.value })}
                    placeholder="e.g., Do I need a permit for this project?"
                    className="mt-1"
                    data-testid="pattern-question"
                  />
                </div>

                {/* Response */}
                <div>
                  <Label>Response * (supports Markdown)</Label>
                  <Textarea
                    value={form.response}
                    onChange={(e) => setForm({ ...form, response: e.target.value })}
                    placeholder="Enter the response the AI should give when this pattern matches..."
                    className="mt-1 min-h-[200px] font-mono text-sm"
                    data-testid="pattern-response"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use **bold**, *italic*, - lists, and line breaks for formatting</p>
                </div>

                {/* Priority and Active */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Priority (0-100)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                      className="mt-1"
                      data-testid="pattern-priority"
                    />
                    <p className="text-xs text-gray-500 mt-1">Higher priority patterns are checked first</p>
                  </div>
                  <div className="flex items-center space-x-2 mt-6">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="h-4 w-4"
                      data-testid="pattern-active"
                    />
                    <Label>Active</Label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" style={{ backgroundColor: COMPANY.primaryColor }} className="text-white" data-testid="save-pattern-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {editingPattern ? 'Update Pattern' : 'Create Pattern'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
